import {BaseScenarioType, IBaseScenario} from '@/lib/types/IBaseScenario';
import {UnitSystem} from '@/interfaces/units';
import {IDistribution} from '@/lib/monteCarlo/interfaces';

export enum PrAxleConfigurationType {
  SINGLE = 'Single',
  TANDEM = 'Tandem',
  TRIDEM = 'Tridem',
  STEER = 'Steer',
}

export interface IPerRoadScenarioBase extends IBaseScenario {
  scenarioType: BaseScenarioType.perRoad,
  unitSystem: UnitSystem,

  district: string | null,
  county: string | null,
  route: string | null,

  description: string | null,

  /**
   *  Loading Conditions
   */

  twoWayAadt: number
  axleGroupsPerDay: number
  pctTrucks: number
  pctTruckGrowthPerYear: number
  pctTrucksInDesignLane: number
  directionalDistribution: number

  // Assumes one entry in each of these variables for each vehicle classification from 4 thru 13

  // Percent of AADT due to vehicles in each class
  vehiclesPctAadt: number[]

  // Number of axles of this type per vehicle on average in each class
  vehicleAxlesSingle: number[]
  vehicleAxlesTandem: number[]
  vehicleAxlesTridem: number[]
  vehicleAxlesSteer: number[]
  roadwayClassification: PrRoadwayClassifications

  // For storing custom loading distributions
  axleLoadDistSingle: number[]
  axleLoadDistTandem: number[]
  axleLoadDistTridem: number[]

  axleSingleEnabled: boolean,
  axleTandemEnabled: boolean,
  axleTridemEnabled: boolean,
  axleSteerEnabled: boolean,
  //
  // summerEnabled: boolean
  // fallEnabled: boolean
  // winterEnabled: boolean
  // springEnabled: boolean
  //
  // summerDuration: number
  // fallDuration: number
  // winterDuration: number
  // springDuration: number
}

export interface IPerRoadScenarioImperial extends IPerRoadScenarioBase {
  layers: IPerRoadLayerImperial[]
  seasons: IPerRoadSeasonImperial[]
  unitSystem: UnitSystem.Imperial

  // summerTemp_degF: number
  // fallTemp_degF: number
  // winterTemp_degF: number
  // springTemp_degF: number
}

export interface IPerRoadScenarioMetric extends IPerRoadScenarioBase {
  layers: IPerRoadLayerMetric[]
  seasons: IPerRoadSeasonMetric[]
  unitSystem: UnitSystem.Metric

  // summerTemp_degC: number
  // fallTemp_degC: number
  // winterTemp_degC: number
  // springTemp_degC: number
}

export enum PrRoadwayClassifications {
  RURAL_INTERSTATE = 'Rural Interstate',
  RURAL_PRINCIPLE_ARTERIAL = 'Rural Principle Arterial',
  RURAL_MINOR_ARTERIAL = 'Rural Minor Arterial',
  RURAL_MINOR_COLLECTOR = 'Rural Minor Collector',
  RURAL_MAJOR_COLLECTOR = 'Rural Major Collector',
  RURAL_LOCAL_COLLECTOR = 'Rural Local Collector',
  URBAN_INTERSTATE = 'Urban Interstate',
  URBAN_OTHER_FREEWAY = 'Urban Other Freeways and Expressways',
  URBAN_PRINCIPLE_ARTERIAL = 'Urban Principle Arterial',
  URBAN_MINOR_ARTERIAL = 'Urban Minor Arterial',
  URBAN_COLLECTOR = 'Urban Collector',
  OTHER = 'Other'
}


/**
 * Layer definition
 */

export enum PerRoadLayerType {
  AC = 'AC',
  CRACKED_AC = 'Cracked AC',
  PCC = 'PCC',
  RUBBLIZED_PCC = 'Rubblized PCC',
  CS_PCC = 'C and S PCC',
  BS_PCC = 'B and S PCC',
  GRANULAR_BASE = 'Granular Base',
  SOIL = 'Soil',
  ROCK = 'Rock',
  OTHER = 'Other'
}

export interface IPerRoadLayerBase {
  id: number
  name: string
  type: PerRoadLayerType
  enabled: boolean

  // Degrees celsius always, increments of 8degC
  pgGradeHigh: number | null
  pgGradeLow: number | null

  poissonRatio: number
}

export interface IPerRoadLayerImperial extends IPerRoadLayerBase {
  unitSystem: UnitSystem.Imperial
  modulus_psi: IDistribution[] // List of seasonal modulus distributions
  // maxModulus_psi: number
  // minModulus_psi: number
  thickness_in: IDistribution

  performanceCriteria: ILayerPerformanceCriteriaImperial[]
}

export interface IPerRoadLayerMetric extends IPerRoadLayerBase {
  unitSystem: UnitSystem.Metric
  modulus_mpa: IDistribution[]
  // maxModulus_mpa: number
  // minModulus_mpa: number
  thickness_cm: IDistribution
  performanceCriteria: ILayerPerformanceCriteriaMetric[]
}

/**
 *  Layer performance criteria
 */

export enum LayerPerformanceCriteriaType {
  STRESS_HORIZONTAL = 'Horizontal Stress',
  STRESS_VERTICAL = 'Vertical Stress',
  STRESS_PRINCIPAL = 'Principal Stress',

  STRAIN_HORIZONTAL = 'Horizontal Strain',
  STRAIN_VERTICAL = 'Vertical Strain',
  STRAIN_PRINCIPAL = 'Principal Strain',

  STRAIN_HORIZONTAL_DISTRIBUTION = 'Horizontal Strain Distribution',

  DEFLECTION_VERTICAL = 'Vertical Deflection'
}

export enum LayerPerformanceCriteriaLocation {
  TOP = 'Top',
  MIDDLE = 'Middle',
  BOTTOM = 'Bottom'
}

export interface ILayerPerformanceCriteria {
  id: number | null
  enabled: boolean
  criteria: LayerPerformanceCriteriaType
  distributionPercentiles: number[] | null
  distribution_ustrain: number[] | null
  strainThreshold_ustrain: number | null
  targetPercentile: number | null
  location: LayerPerformanceCriteriaLocation
  useTransferFunction: boolean
  k1: number | null
  k2: number | null
}

export interface ILayerPerformanceCriteriaImperial extends ILayerPerformanceCriteria {
  unitSystem: UnitSystem.Imperial
  stressThreshold_psi: number | null
  deflectionThreshold_in: number | null
}

export interface ILayerPerformanceCriteriaMetric extends ILayerPerformanceCriteria {
  unitSystem: UnitSystem.Metric
  stressThreshold_mpa: number | null
  deflectionThreshold_cm: number | null
}

/*
 Seasons
 */

export interface IPerRoadSeason {
  id: number | null
  name: string
  duration_weeks: number
  enabled: boolean
}

export interface IPerRoadSeasonImperial extends IPerRoadSeason {
  temp_degF: number
  unitSystem: UnitSystem.Imperial
}

export interface IPerRoadSeasonMetric extends IPerRoadSeason {
  temp_degC: number
  unitSystem: UnitSystem.Metric
}
